import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { UomLadderResolved, UomLadderRow } from '../../services/api/uomLadderService';
import {
  fetchUomLadderList,
  resolveUomLadder,
  createUomLadder,
  supersedeUomLadder,
  deleteUomLadder,
} from '../thunks/uomLadderThunks';

export interface UomLadderFilters {
  scopePathPrefix: string;
  scopeLevel: number | null;     // null = any
  includeExpired: boolean;
}

export interface UomLadderState {
  list: {
    items: UomLadderRow[];
    pageIndex: number;
    pageSize: number;
    totalCount: number;
    isLoading: boolean;
    error: string | null;
  };
  /** Cache keyed by `resolveCacheKey()`. `null` = miss (fetched + empty). */
  resolved: Record<string, UomLadderResolved | null>;
  filters: UomLadderFilters;
}

const initialState: UomLadderState = {
  list: {
    items: [],
    pageIndex: 1,
    pageSize: 50,
    totalCount: 0,
    isLoading: false,
    error: null,
  },
  resolved: {},
  filters: {
    scopePathPrefix: '',
    scopeLevel: null,
    includeExpired: false,
  },
};

const uomLadderSlice = createSlice({
  name: 'uomLadder',
  initialState,
  reducers: {
    clearError: (state) => { state.list.error = null; },
    setFilter: <K extends keyof UomLadderFilters>(
      state: UomLadderState,
      action: PayloadAction<{ key: K; value: UomLadderFilters[K] }>
    ) => {
      state.filters[action.payload.key] = action.payload.value;
    },
    clearFilters: (state) => { state.filters = { ...initialState.filters }; },
    invalidateResolved: (state) => { state.resolved = {}; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUomLadderList.pending, (state) => {
        state.list.isLoading = true;
        state.list.error = null;
      })
      .addCase(fetchUomLadderList.fulfilled, (state, action) => {
        state.list.isLoading = false;
        state.list.items = action.payload.items;
        state.list.pageIndex = action.payload.pageIndex;
        state.list.pageSize = action.payload.pageSize;
        state.list.totalCount = action.payload.totalCount;
      })
      .addCase(fetchUomLadderList.rejected, (state, action) => {
        state.list.isLoading = false;
        state.list.error = action.payload as string;
      });

    builder
      .addCase(resolveUomLadder.fulfilled, (state, action) => {
        state.resolved[action.payload.key] = action.payload.data;
      });

    // Successful writes invalidate the per-item resolved cache so the
    // workbench popover refetches on next open. Coarse but cheap.
    builder
      .addCase(createUomLadder.fulfilled, (state) => { state.resolved = {}; })
      .addCase(supersedeUomLadder.fulfilled, (state) => { state.resolved = {}; })
      .addCase(deleteUomLadder.fulfilled, (state, action) => {
        state.resolved = {};
        state.list.items = state.list.items.filter((x) => x.id !== action.payload);
        state.list.totalCount = Math.max(0, state.list.totalCount - 1);
      });
  },
});

export const { clearError, setFilter, clearFilters, invalidateResolved } = uomLadderSlice.actions;
export default uomLadderSlice.reducer;
